import { useEffect, useState } from 'react';
import { FAVORITES_STORAGE_KEY } from '../../../constants';
import { Favorite } from '../types';

/**
 * Carteira do usuário, persistida no localStorage.
 *
 * Só grava depois que os dados da tabela carregaram (canSave), e acompanha
 * alterações feitas em outras abas do navegador.
 */
export function useFavorites(canSave: boolean) {
  const [favorites, setFavorites] = useState<Favorite[]>([]);

  useEffect(() => {
    const stored = localStorage.getItem(FAVORITES_STORAGE_KEY);
    if (stored) {
      setFavorites(JSON.parse(stored));
    }
  }, []);

  useEffect(() => {
    if (canSave) {
      localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(favorites));
    }
  }, [favorites, canSave]);

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === FAVORITES_STORAGE_KEY) {
        setFavorites(event.newValue ? JSON.parse(event.newValue) : []);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  /** Adiciona o ativo à carteira, ou remove se já estiver nela. */
  const toggleFavorite = (ticker: string) => {
    setFavorites((prev) =>
      prev.some((f) => f.ticker === ticker)
        ? prev.filter((f) => f.ticker !== ticker)
        : [...prev, { ticker, quantity: 0 }]
    );
  };

  /** Atualiza quantidade e meta de um ativo (comparando o ticker sem diferenciar maiúsculas). */
  const updateFavorite = (newItem: Favorite) => {
    setFavorites((prev) => {
      const tickerUpper = newItem.ticker.toUpperCase();
      const exists = prev.some((f) => f.ticker.toUpperCase() === tickerUpper);

      if (exists) {
        return prev.map((f) =>
          f.ticker.toUpperCase() === tickerUpper
            ? { ticker: f.ticker, quantity: newItem.quantity, goal: newItem.goal } // mantém a grafia original
            : f
        );
      }

      return [...prev, newItem];
    });
  };

  const isFavorite = (ticker: string) => favorites.some((f) => f.ticker === ticker);

  return { favorites, isFavorite, toggleFavorite, updateFavorite };
}
